import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { getTranscription } from '@/api/api'
import { apiClient } from '@/api/client'
import { saveTranscription } from '@/api/services/transcription'
import { SegmentResponse, TranscribeResponse } from '@/api/type'
import { SILENCE_TEXT } from '@/lib/constants'
import { deleteAudio, getAudio, getOfflineEpisode, saveAudio, updateTranscription } from '@/lib/db'

const SILENCE_GAP_THRESHOLD = 1.0

/**
 * 音声認識と無音期間処理、IndexedDBからの読み込みを行うカスタムフック
 */
export const useTranscribe = (
  domain: string,
  productId: string,
  episodeId: string,
  duration: number,
  baseUrl: string,
) => {
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [audioUrl, setAudioUrl] = useState<string | null>(null) // Web Audio API用（Blob URL）
  const [html5AudioUrl, setHtml5AudioUrl] = useState<string | null>(null) // HTML5 audio用（ネットワークURL）
  const [transcription, setTranscription] = useState<TranscribeResponse | null>(null)
  const [isLoadingFromDb, setIsLoadingFromDb] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const isDirty = useRef(false)

  // 外部（セグメント編集）から呼ばれるsetter。BE保存をトリガーするためdirtyフラグを立てる
  const setTranscriptionDirty = useCallback((t: TranscribeResponse | null) => {
    isDirty.current = true
    setTranscription(t)
  }, [])

  const handleTranscribe = useCallback(async (): Promise<TranscribeResponse | null> => {
    setIsTranscribing(true)
    try {
      const result = await getTranscription(episodeId)

      // 無音期間を追加したセグメントを作成（単語ベース）
      const segmentsWithSilence: SegmentResponse[] = []

      // 全ての単語を収集
      const allWords: Array<{ word: string; start: number; end: number; segmentIndex: number }> = []
      result.segments.forEach((segment, segmentIndex) => {
        if (segment.words && segment.words.length > 0) {
          segment.words.forEach((word) => {
            allWords.push({ ...word, segmentIndex })
          })
        }
      })

      if (allWords.length === 0) {
        return result
      }

      // 音声開始から最初の単語までの無音期間
      const firstWord = allWords[0]
      if (firstWord.start > SILENCE_GAP_THRESHOLD) {
        segmentsWithSilence.push({
          start: 0,
          end: firstWord.start,
          text: SILENCE_TEXT,
          words: [],
          seek: result.segments[0]?.seek || 0,
          no_speech_prob: 1.0,
        })
      }

      // 各セグメントを処理
      for (let i = 0; i < result.segments.length; i++) {
        const segment = result.segments[i]

        if (!segment.words || segment.words.length === 0) {
          segmentsWithSilence.push(segment)
          continue
        }

        // セグメント内の単語間の無音期間をチェック
        const segmentWords = segment.words
        const wordsWithSilence: typeof segment.words = []

        for (let j = 0; j < segmentWords.length; j++) {
          const currentWord = segmentWords[j]

          // 前の単語との間に無音があるかチェック
          if (j > 0) {
            const prevWord = segmentWords[j - 1]
            const silenceGap = currentWord.start - prevWord.end

            if (silenceGap > SILENCE_GAP_THRESHOLD) {
              // 無音セグメントを追加
              segmentsWithSilence.push({
                start: prevWord.end,
                end: currentWord.start,
                text: SILENCE_TEXT,
                words: [],
                seek: segment.seek,
                no_speech_prob: 1.0,
              })

              // 現在の単語から新しいセグメントを開始
              if (wordsWithSilence.length > 0) {
                segmentsWithSilence.push({
                  ...segment,
                  start: wordsWithSilence[0].start,
                  end: wordsWithSilence[wordsWithSilence.length - 1].end,
                  text: wordsWithSilence.map((w) => w.word).join(' '),
                  words: wordsWithSilence,
                })
                wordsWithSilence.length = 0
              }
            }
          }

          wordsWithSilence.push(currentWord)
        }

        // 残りの単語でセグメントを作成
        if (wordsWithSilence.length > 0) {
          segmentsWithSilence.push({
            ...segment,
            start: wordsWithSilence[0].start,
            end: wordsWithSilence[wordsWithSilence.length - 1].end,
            text: wordsWithSilence.map((w) => w.word).join(' '),
            words: wordsWithSilence,
          })
        }

        // 次のセグメントの最初の単語との間に無音があるかチェック
        if (i < result.segments.length - 1) {
          const nextSegment = result.segments[i + 1]
          if (nextSegment.words && nextSegment.words.length > 0) {
            const lastWord = segment.words[segment.words.length - 1]
            const nextFirstWord = nextSegment.words[0]
            const silenceGap = nextFirstWord.start - lastWord.end

            if (silenceGap > SILENCE_GAP_THRESHOLD) {
              segmentsWithSilence.push({
                start: lastWord.end,
                end: nextFirstWord.start,
                text: SILENCE_TEXT,
                words: [],
                seek: segment.seek,
                no_speech_prob: 1.0,
              })
            }
          }
        }
      }

      // 最後の単語から音声終了までの無音期間
      const lastWord = allWords[allWords.length - 1]
      const lastSegment = result.segments[result.segments.length - 1]
      // 音声ファイルの実際の長さを取得
      const audioEnd = duration || lastSegment?.end || lastWord.end
      if (audioEnd - lastWord.end > SILENCE_GAP_THRESHOLD) {
        segmentsWithSilence.push({
          start: lastWord.end,
          end: audioEnd,
          text: SILENCE_TEXT,
          words: [],
          seek: lastSegment?.seek || 0,
          no_speech_prob: 1.0,
        })
      }

      return {
        ...result,
        segments: segmentsWithSilence,
      }
    } catch (error) {
      console.error('Failed to transcribe:', error)
      toast.error('音声認識に失敗しました')
      return null
    } finally {
      setIsTranscribing(false)
    }
  }, [episodeId, duration])

  // BlobからaudioUrl（Data URL / Blob URL）を生成するヘルパー
  const setBlobAsAudioUrl = useCallback((audioBlob: Blob) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    if (isIOS) {
      console.log('[Audio] iOS detected, using Blob URL for Web Audio')
      setAudioUrl(URL.createObjectURL(audioBlob))
    } else {
      const reader = new FileReader()
      reader.onload = () => {
        console.log('[Audio] Data URL created successfully')
        setAudioUrl(reader.result as string)
      }
      reader.onerror = (error) => {
        console.error('[Audio] FileReader error:', error)
        setAudioUrl(URL.createObjectURL(audioBlob))
      }
      reader.readAsDataURL(audioBlob)
    }
  }, [])

  // オフライン時は文字起こしAPIをスキップし、キャッシュ済みデータのみ使用
  const tryTranscribe = useCallback(async (): Promise<TranscribeResponse | null> => {
    if (!navigator.onLine) {
      console.log('[Audio] Offline: skipping transcription API')
      return null
    }
    return handleTranscribe()
  }, [handleTranscribe])

  // IndexedDBから音声とTranscribeデータを読み込み、なければ自動保存
  useEffect(() => {
    const loadFromDb = async () => {
      setIsLoadingFromDb(true)
      try {
        const rawStored = await getAudio(domain, productId, episodeId)

        // 無効なデータ（text/htmlなど）がキャッシュされていた場合はクリアして再取得
        const isInvalidBlob = rawStored?.audioBlob.type != null && !rawStored.audioBlob.type.startsWith('audio/')
        if (isInvalidBlob) {
          console.warn('[Audio] Invalid cached blob type detected, clearing cache:', rawStored!.audioBlob.type)
          await deleteAudio(domain, productId, episodeId)
        }

        const stored = isInvalidBlob ? null : rawStored

        if (stored) {
          console.log('[Audio] Loading from IndexedDB (audios)', {
            blobSize: stored.audioBlob.size,
            blobType: stored.audioBlob.type,
          })

          // iOS Safari対応: BlobのMIMEタイプを確認・修正
          let audioBlob = stored.audioBlob
          if (!audioBlob.type || audioBlob.type === '') {
            console.warn('[Audio] Blob has no MIME type, setting to audio/mpeg')
            audioBlob = new Blob([audioBlob], { type: 'audio/mpeg' })
          }

          // HTML5 audio用URL: 常にBlob URLを使用（ネットワークURLは認証ヘッダーが付かずエラーになるため）
          setHtml5AudioUrl(URL.createObjectURL(audioBlob))
          setBlobAsAudioUrl(audioBlob)

          // BEから最新を取得（オンライン時）、オフラインならIndexedDBのキャッシュを使用
          const result = await tryTranscribe()
          if (result) {
            setTranscription(result)
          } else if (stored.transcription) {
            setTranscription(stored.transcription)
          }
          setIsInitialLoad(false)
          return
        }

        // audiosストアに無い場合、offlineEpisodesストアをフォールバック検索
        const offlineEp = await getOfflineEpisode(Number(episodeId))
        if (offlineEp) {
          console.log('[Audio] Loading from IndexedDB (offlineEpisodes)', {
            episodeId: offlineEp.episodeId,
            blobSize: offlineEp.blob.size,
            mimeType: offlineEp.mimeType,
          })

          let audioBlob = offlineEp.blob
          // MIMEタイプが音声でない場合に修正
          if (!audioBlob.type || !audioBlob.type.startsWith('audio/')) {
            audioBlob = new Blob([audioBlob], { type: offlineEp.mimeType || 'audio/mpeg' })
          }

          setBlobAsAudioUrl(audioBlob)
          // html5AudioUrlにもBlob URLを設定（オフラインではネットワークURL不可）
          setHtml5AudioUrl(URL.createObjectURL(audioBlob))

          // audiosストアにもコピーして次回以降の読み込みを高速化
          await saveAudio(domain, productId, episodeId, episodeId, audioBlob, null)

          // Transcribeデータはオフライン時スキップ
          const result = await tryTranscribe()
          if (result) {
            setTranscription(result)
          }
          setIsInitialLoad(false)
          return
        }

        // どちらのストアにも無い場合はネットワークから取得して自動保存
        try {
          console.log('[Audio] Fetching from network:', `${baseUrl}/file`)
          const response = await apiClient.get<Blob>(`${baseUrl}/file`, { responseType: 'blob' })

          // Content-Type が音声でない場合はキャッシュせずエラー（404 → index.html 対策）
          const contentType = (response.headers['content-type'] as string | undefined) ?? ''
          if (!contentType.startsWith('audio/')) {
            console.error('[Audio] Server returned non-audio content-type:', contentType)
            throw new Error(`[Audio] Invalid content-type: ${contentType}`)
          }

          const blob = response.data
          const audioBlob = new Blob([blob], { type: contentType })

          // HTML5 audio用: Blob URLを設定（ネットワークURLは認証ヘッダーが付かずエラーになるため）
          setHtml5AudioUrl(URL.createObjectURL(audioBlob))

          console.log('[Audio] Saving to IndexedDB', {
            blobSize: audioBlob.size,
            blobType: audioBlob.type,
          })
          await saveAudio(domain, productId, episodeId, episodeId, audioBlob, null)

          setBlobAsAudioUrl(audioBlob)
        } catch (saveError) {
          console.error('[Audio] Failed to auto-save audio:', saveError)
          toast.error('音声の自動保存に失敗しました')
        }

        // Transcribeデータを自動実行
        const result = await tryTranscribe()
        if (result) {
          setTranscription(result)
        }
        setIsInitialLoad(false)
      } catch (error) {
        console.error('[Audio] Failed to load from IndexedDB:', error)
        toast.error('音声の読み込みに失敗しました')
      } finally {
        setIsLoadingFromDb(false)
      }
    }

    loadFromDb()

    // Blob URLのクリーンアップ
    return () => {
      if (audioUrl && audioUrl.startsWith('blob:')) {
        console.log('[Audio] Revoking Blob URL:', audioUrl)
        URL.revokeObjectURL(audioUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, domain, episodeId, productId])

  // transcriptionが更新されたら自動的にIndexedDB + BEに保存（初回ロード後のみ）
  const beSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!transcription || isInitialLoad) return

    // IndexedDB: 即時保存
    const saveTranscriptionToDb = async () => {
      try {
        await updateTranscription(domain, productId, episodeId, transcription)
        console.log('Transcription auto-saved to IndexedDB')
      } catch (error) {
        console.error('Failed to auto-save transcription:', error)
      }
    }
    saveTranscriptionToDb()

    // BE: ユーザー編集時のみ debounce 2秒で保存
    if (!isDirty.current) return
    if (beSaveTimer.current) clearTimeout(beSaveTimer.current)
    beSaveTimer.current = setTimeout(() => {
      if (!navigator.onLine) return
      isDirty.current = false
      const filtered: TranscribeResponse = {
        ...transcription,
        segments: transcription.segments.filter((s) => s.text !== SILENCE_TEXT),
      }
      saveTranscription(episodeId, filtered)
        .then(() => console.log('Transcription auto-saved to BE'))
        .catch((error) => console.error('Failed to auto-save transcription to BE:', error))
    }, 2000)

    return () => {
      if (beSaveTimer.current) clearTimeout(beSaveTimer.current)
    }
  }, [transcription, domain, productId, episodeId, isInitialLoad])

  return {
    handleTranscribe,
    isTranscribing,
    audioUrl, // Web Audio API用（Blob URL）
    html5AudioUrl, // HTML5 audio用（ネットワークURL）
    transcription,
    setTranscription: setTranscriptionDirty,
    isLoadingFromDb,
    isInitialLoad,
  }
}
